import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { Button, Drawer } from "@shared/ui";
import { useEditorDocumentImport } from "@shared/ui/Editor/lib/useEditorDocumentImport";
import { DocumentImportOverlay } from "@shared/ui/Editor/ui/DocumentImportOverlay";
import { DocumentUploadButton } from "@shared/ui/Editor/ui/DocumentUploadButton";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import Editor from "@shared/ui/Editor/ui";
import { Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import styles from "../drawer.module.scss";
import { useEditorDownload } from "../lib/useEditorDownload";
import { BriefDownloadMenu } from "./BriefDownloadMenu";

interface CreatorBriefEditorDrawerProps {
  open: boolean;
  initialHtml: string;
  isSaving: boolean;
  onClose: () => void;
  onSave: (html: string) => void;
}

export function CreatorBriefEditorDrawer({
  open,
  initialHtml,
  isSaving,
  onClose,
  onSave,
}: CreatorBriefEditorDrawerProps) {
  const { brief } = useBriefStore();
  const editorRef = useRef<EditorHandle>(null);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusTone, setStatusTone] = useState<"ok" | "err">("ok");

  const showStatus = (message: string, tone: "ok" | "err" = "ok") => {
    setStatusTone(tone);
    setStatusMessage(message);
  };

  const { isDownloading, downloadLabel, handleDownloadDoc, handleDownloadPdf } =
    useEditorDownload({
      editorRef,
      campaignName: brief?.name || "Creator brief",
      showStatus,
      onMenuClose: () => undefined,
    });

  const documentImport = useEditorDocumentImport({
    editorRef,
    disabled: isDownloading || isSaving,
    showStatus,
    onImportComplete: () => setIsMediaUploading(false),
  });

  const isBusy =
    isSaving || isMediaUploading || isDownloading || documentImport.isImporting;

  useEffect(() => {
    if (!statusMessage || isBusy) return;
    const timer = setTimeout(() => setStatusMessage(null), 3200);
    return () => clearTimeout(timer);
  }, [statusMessage, isBusy]);

  useEffect(() => {
    if (!open) setStatusMessage(null);
  }, [open]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={720}
      zIndex={1100}
      push={false}
      destroyOnClose
      getContainer={() => document.body}
      rootClassName={styles.drawerRoot}
      classNames={{
        wrapper: styles.wrapper,
        content: styles.content,
        header: styles.header,
        body: styles.body,
        footer: styles.footerBar,
      }}
      title={
        <div className={styles.titleBlock}>
          <span className={styles.title}>Creator brief</span>
          <span className={styles.subtitle}>
            Press <kbd className={styles.kbd}>/</kbd> for blocks · upload DOC or
            PDF
          </span>
        </div>
      }
      extra={
        <div className={styles.headerActions}>
          <DocumentUploadButton
            fileInputRef={documentImport.fileInputRef}
            accept={documentImport.accept}
            disabled={isBusy}
            isImporting={documentImport.isImporting}
            onFileInputChange={documentImport.onFileInputChange}
            onClick={documentImport.openFilePicker}
          />
          <BriefDownloadMenu
            isDownloading={isDownloading}
            onDownloadDoc={handleDownloadDoc}
            onDownloadPdf={handleDownloadPdf}
          />
        </div>
      }
      footer={
        <div className={styles.footer}>
          <Button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={isSaving || isMediaUploading || documentImport.isImporting}
            disabled={isBusy}
            onClick={() => {
              const html =
                editorRef.current?.getHTML({ syncTokens: true }) ?? "";
              onSave(html);
            }}
            className={styles.saveButton}
          >
            Save Changes
          </Button>
        </div>
      }
    >
      {statusMessage && (
        <div
          className={`${styles.statusBanner} ${
            statusTone === "err" ? styles.statusErr : styles.statusOk
          }`}
        >
          <span>{statusMessage}</span>
          <button
            type="button"
            onClick={() => setStatusMessage(null)}
            className={styles.dismissButton}
            aria-label="Dismiss"
          >
            <X size={12} />
          </button>
        </div>
      )}
      <div
        className={`${styles.editorWrap} ${documentImport.isDragOver ? styles.editorWrapDrag : ""}`}
        onDragOverCapture={documentImport.onDragOver}
        onDragLeave={documentImport.onDragLeave}
        onDropCapture={documentImport.onDrop}
        onPasteCapture={documentImport.onPaste}
      >
        <DocumentImportOverlay
          isImporting={documentImport.isImporting}
          isDragOver={documentImport.isDragOver}
        />
        {isDownloading && (
          <div className={styles.importOverlay}>
            <div className={styles.importPill}>
              <Loader2 size={15} className={styles.spinIcon} />
              {downloadLabel}
            </div>
          </div>
        )}
        {open ? (
          <Editor
            key={initialHtml ? "brief" : "new"}
            ref={editorRef}
            initialHtml={initialHtml}
            editable={!isDownloading}
            allowDocumentImport
            onMediaUploadStart={() => setIsMediaUploading(true)}
            onMediaUploadComplete={() => setIsMediaUploading(false)}
          />
        ) : null}
      </div>
    </Drawer>
  );
}
